/**
 * Workflow Template Shortcut — load any of the 7 templates from
 * `gibbs/workflow/templates.js` by name from natural-language input.
 *
 * Pre-2026-05-03 only `hit-to-lead` was reachable via NL (through the
 * campaign-verb shortcut), and `de-novo-generation` partially via the
 * de-novo shortcut. The other 5 templates (fragment-search-filtered,
 * selectivity-analysis, ai-folding-design, building-block-search,
 * workspace-save-resume) had no NL routing at all — only available via
 * the workflow library UI.
 *
 * This shortcut closes that gap: a single matcher fuzzy-routes phrases
 * like "load the X template", "open the X workflow", "use the X
 * pipeline" to the named template, then emits a workflow plan with the
 * template's full action sequence and connection graph.
 *
 * **Position in chain:** BEFORE the campaign shortcut, so explicit
 * template-loading phrasings ("load the hit-to-lead template") win
 * over campaign-verb routing ("hit-to-lead" → campaign workflow).
 *
 * @module gibbs/knowledge/workflowTemplateShortcut
 */

import { getAllTemplates, getTemplate } from '../workflow/templates';

// ─── Trigger word + name token detection ───────────────────────────────────

/**
 * Words that signal "this is a template/workflow loading request" rather
 * than a campaign verb or workflow-class shortcut. Required for the
 * matcher to fire — keeps it disjoint from the campaign / workflow-class
 * tiers above.
 */
const TRIGGER_WORDS = [
    'template',
    'workflow',
    'pipeline',
    'recipe',
];

/**
 * Verbs that, paired with a trigger word, indicate a load/open intent
 * vs. a query intent. We don't actually filter on verbs (the trigger
 * word is enough), but the list documents what the matcher accepts.
 *
 * Accepted phrasings include: "load the X template", "open the X
 * workflow", "use the X pipeline", "start the X workflow", "give me
 * the X template", "run the X recipe".
 */
// const ACCEPT_VERBS = ['load', 'open', 'use', 'start', 'run', 'give', 'show'];

/**
 * Per-template aliases. The matcher requires (a) one of the TRIGGER_WORDS
 * AND (b) a unique alias from this map to fire. The first template whose
 * aliases match wins (the order here matches the templates.js order).
 *
 * Aliases are matched as case-insensitive substrings against the raw
 * user message. They are kept TIGHT to avoid bleed-over: e.g.,
 * `selectivity-analysis` aliases include "selectivity" but NOT bare
 * "analysis" (which is too generic).
 */
const TEMPLATE_ALIASES = {
    'hit-to-lead': [
        'hit-to-lead',
        'hit to lead',
        'h2l',
    ],
    'fragment-search-filtered': [
        'fragment search',
        'fragment-search',
        'fragment search filter',
        'fragment search with filter',
        'filtered fragment',
        'fbdd',
    ],
    'selectivity-analysis': [
        'selectivity-analysis',
        'selectivity analysis',
        'selectivity',
        'multi-protein',
        'multi protein',
        'off-target',
    ],
    'de-novo-generation': [
        'de novo generation',
        'de-novo generation',
        'de novo + screen',
        'reinvent + screen',
        'reinvent and screen',
        'generation + screen',
    ],
    'ai-folding-design': [
        'ai folding',
        'ai-folding',
        'folding design',
        'alphafold design',
        'boltz design',
        'fold and design',
    ],
    'building-block-search': [
        'building block',
        'building-block',
        'vendor library',
        'reaction-aware',
        'reaction aware',
    ],
    'workspace-save-resume': [
        'workspace save',
        'save workspace',
        'workspace resume',
        'resume workspace',
        'save and resume',
        'workspace state',
    ],
};

/**
 * Match a user message against the template alias index.
 *
 * @param {string} userMessage
 * @returns {string|null} the matched template id, or null
 */
export function matchWorkflowTemplateIntent(userMessage) {
    if (!userMessage || typeof userMessage !== 'string') return null;
    const lower = userMessage.toLowerCase();

    // Require a trigger word — otherwise fall through to other tiers.
    const hasTrigger = TRIGGER_WORDS.some((w) => lower.includes(w));
    if (!hasTrigger) return null;

    // First-match wins (templates ordered by templates.js).
    for (const [templateId, aliases] of Object.entries(TEMPLATE_ALIASES)) {
        for (const alias of aliases) {
            if (lower.includes(alias)) return templateId;
        }
    }

    return null;
}

/**
 * Detect a workflow-template alias in the user's message WITHOUT requiring
 * a trigger word. Used by the disease cascade and anchor resolver to thread
 * the user's declared workflow intent through their target_selection
 * clarification context, so the resume handler can emit a multi-step plan
 * (load + dock + score + grow + …) instead of just the acquire step.
 *
 * Contrast with `matchWorkflowTemplateIntent` (which gates on trigger word):
 * this function is for INTERNAL chain plumbing, not for the routing chain's
 * tier 0a2 decision. The trigger-word disjointness still applies for the
 * standalone template-loading shortcut.
 *
 * 2026-06-02: introduced as part of the disease cascade workflow chaining
 * UX fix. See labwork/studies/gibbs-disease-cascade-rca/rca.md and
 * Agent_Bmaps.md "Disease cascade workflow chaining UX" follow-up.
 *
 * @param {string} userMessage
 * @returns {string|null} the matched template id, or null
 */
export function detectTemplateAlias(userMessage) {
    if (!userMessage || typeof userMessage !== 'string') return null;
    const lower = userMessage.toLowerCase();
    for (const [templateId, aliases] of Object.entries(TEMPLATE_ALIASES)) {
        for (const alias of aliases) {
            if (lower.includes(alias)) return templateId;
        }
    }
    return null;
}

// ─── Plan emission ─────────────────────────────────────────────────────────

/**
 * Convert a template's `steps` + `connections` into the action-plan shape
 * used by the chain (matches dockAndScoreShortcut, hitDiscoveryShortcut,
 * etc.): each step gets `params` (from `paramDefaults`), `inputsFrom`
 * (computed from the connection graph as `step:N.output` references),
 * and a 1-indexed `step` field.
 *
 * **Important: step-to-step refs go in `inputsFrom`, not `resolvedInputs`.**
 * The DAG builder at `createFromActionPlan` (builders.js) reads explicit
 * step-to-step references from `inputsFrom` to create edges between
 * nodes — it does NOT read them from `resolvedInputs`. An earlier
 * version of this function placed the refs in `resolvedInputs`, which
 * caused two failure modes:
 *
 *   1. `createFromActionPlan` saw empty `inputsFrom`, fell back to
 *      type-based auto-mapping between consecutive nodes. For steps
 *      where the auto-mapping found no compatible output→input pair,
 *      no edge was created at all and downstream input resolution
 *      failed.
 *   2. The `step:N.X` strings sitting in `resolvedInputs` leaked
 *      through `paramOverrides` (because builders.js merges
 *      `planned.resolvedInputs` into `paramOverrides`) and reached
 *      action handlers as literal strings instead of resolved values.
 *
 * Inputs that are not satisfied by any connection are left absent from
 * `inputsFrom` so the workflow checker UI can prompt the user.
 *
 * Regression guard: `proto/test/gibbs/templatePlanEdges.test.js`.
 *
 * @param {string} templateId
 * @returns {Object|null} workflow-intent plan or null if template missing
 */
export function buildPlanFromTemplate(templateId) {
    const template = getTemplate(templateId);
    if (!template) return null;

    // For each step, collect its input-name → step-ref map from the
    // template's connection graph.
    const inputRefsByStep = template.steps.map(() => ({}));
    for (const conn of template.connections || []) {
        if (typeof conn.toStep !== 'number'
            || typeof conn.fromStep !== 'number') continue;
        const ref = `step:${conn.fromStep + 1}.${conn.outputName}`;
        inputRefsByStep[conn.toStep][conn.inputName] = ref;
    }

    const actions = template.steps.map((step, i) => {
        const action = {
            actionId: step.actionId,
            params: { ...(step.paramDefaults || {}) },
            resolvedInputs: {},
            step: i + 1,
        };
        // Only set inputsFrom when there are refs — keeps the action
        // shape clean for the first step (no upstream inputs) and for
        // any step the connection graph leaves unwired.
        if (Object.keys(inputRefsByStep[i]).length > 0) {
            action.inputsFrom = { ...inputRefsByStep[i] };
        }
        return action;
    });

    return {
        intent: 'workflow',
        summary: `${template.name} — ${template.description}`,
        actions,
        templateId: template.id,
    };
}

// ─── Top-level entry point ─────────────────────────────────────────────────

/**
 * Try to resolve a workflow-template loading intent into a plan.
 *
 * Returns one of:
 *   - { intent: 'workflow', summary, actions, templateId }
 *       — ready to dispatch into the workflow checker
 *   - null
 *       — phrase did not match a template trigger; fall through to the
 *       campaign/workflow-class/anchor/LLM tiers below
 *
 * No workspace preconditions are checked here. The template's load-*
 * setup steps (load-protein-from-pdb, load-compound, etc.) deliberately
 * have empty paramDefaults so the user fills in PDB IDs and compound
 * inputs through the workflow checker UI.
 *
 * @param {string} userMessage
 * @returns {Object|null}
 */
export function tryWorkflowTemplateShortcut(userMessage) {
    const templateId = matchWorkflowTemplateIntent(userMessage);
    if (!templateId) return null;
    return buildPlanFromTemplate(templateId);
}

/**
 * @returns {string[]} all template ids known to the alias index
 */
export function getKnownTemplateIds() {
    return Object.keys(TEMPLATE_ALIASES);
}

// Re-export for tests that want to inspect the alias index.
export { TEMPLATE_ALIASES, TRIGGER_WORDS };

export default {
    matchWorkflowTemplateIntent,
    detectTemplateAlias,
    buildPlanFromTemplate,
    tryWorkflowTemplateShortcut,
    getKnownTemplateIds,
    TEMPLATE_ALIASES,
    TRIGGER_WORDS,
};

// Static guard: every alias index entry must be a real template.
// Runs at module load — surfaces drift between this file and templates.js
// before any user request can hit a stale alias.
{
    const knownIds = new Set(getAllTemplates().map((t) => t.id));
    for (const id of Object.keys(TEMPLATE_ALIASES)) {
        if (!knownIds.has(id)) {
            throw new Error(
                `workflowTemplateShortcut: alias index references "${id}" `
                + 'which is not in templates.js. Either add the template '
                + 'or remove the alias entry.'
            );
        }
    }
}
